import React, {
  CSSProperties,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import type { ILinkedUser } from '@/app-package';
import { Icon, Text } from '@bb/elements-web';
import { Button, Dropdown, MenuProps, message, Spin } from 'antd';
import { AuthContext } from 'contexts';
import { useDialogStore } from 'hooks/dialogStore/useDialogStore';
import { DIALOG_KEYS } from 'modals/keys';
import LinkAccountModal from 'modals/LinkAccountModal/LinkAccountModal';
import { switchAccount } from 'api/UserController';
import { useMutation } from '@tanstack/react-query';
import { Avatar } from '../Avatar';
import theme from '../theme';

const AccountLinker = () => {
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const { auth } = useContext(AuthContext);
  const { id: currentId, linkedAccounts } = auth || {};
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { addDialog } = useDialogStore();

  const { mutate: switchAccountMutation } = useMutation({
    mutationFn: switchAccount,
    onSuccess: () => window.location.reload(),
    onError: () => {
      setLoadingId(null);
      message.error(
        'Unable to switch accounts, if the issue persists please contact support.',
      );
    },
  });

  const onLinkAccount = useCallback(() => {
    addDialog({
      component: <LinkAccountModal key={DIALOG_KEYS.LinkAccountModal} />,
      key: DIALOG_KEYS.LinkAccountModal,
    });
  }, [addDialog]);

  const getSubTitle = (account: ILinkedUser) => {
    if (!account) return '--';
    const { brokerageName, mlsNumber, officeId } = account;
    return mlsNumber ?? officeId ?? brokerageName ?? '--';
  };

  const items: MenuProps['items'] = useMemo(() => {
    const accounts = linkedAccounts?.map(account => ({
      key: account.id,
      disabled: !!loadingId,
      label: (
        <StyledItem
          onClick={() => {
            if (currentId !== account.id && !loadingId) {
              setLoadingId(account.id);
              switchAccountMutation(account.id);
            }
          }}
          key={account.id}
        >
          <StyledAvatarContainer>
            <Avatar
              backgroundColor="background"
              src={account?.logo}
              user={{ firstName: linkedAccounts[0]?.board }}
            />
          </StyledAvatarContainer>
          <StyledMenuContent>
            <StyledFlex>
              <StyledTitle
                thickness={currentId === account.id ? 'bold' : 'normal'}
                ellipsis
                $dark
                $inMenu
                style={{ lineHeight: 1.1 }}
              >
                {account.board}
              </StyledTitle>
              <StyledSubTitle
                size="small"
                ellipsis
                $dark
                style={{ lineHeight: 1.1 }}
              >
                {getSubTitle(account)}
              </StyledSubTitle>
            </StyledFlex>
            {!loadingId && currentId === account.id && (
              <Icon icon="checkOutlined" color="primary" />
            )}
            {loadingId && loadingId === account.id && (
              <StyledSpin size="small" />
            )}
          </StyledMenuContent>
        </StyledItem>
      ),
    }));

    return [
      ...(accounts || []),
      {
        key: 'new-account',
        disabled: !!loadingId,
        style: itemInlay,
        label: (
          <StyledButtonContainer onClick={onLinkAccount}>
            <Icon icon="plusCircleOutlined" color="primary" />
            <StyledLink color="primary">Link another account</StyledLink>
          </StyledButtonContainer>
        ),
      },
    ];
  }, [
    currentId,
    linkedAccounts,
    loadingId,
    onLinkAccount,
    switchAccountMutation,
  ]);

  return (
    <StyledContainer ref={containerRef}>
      <Dropdown
        menu={{
          items,
          style: stylesMenu,
        }}
        overlayStyle={stylesOverlay}
        trigger={['click']}
        getPopupContainer={triggerNode =>
          containerRef.current ?? triggerNode.parentElement ?? document.body
        }
      >
        <StyledButton type="default">
          {/* <> is creating a span for some reason */}
          {linkedAccounts[0]?.board && (
            <StyledTitle thickness="bold" ellipsis>
              {linkedAccounts[0].board}
            </StyledTitle>
          )}
          {linkedAccounts[0]?.board && <StyledDivider />}
          <StyledSubTitle size="small" ellipsis>
            {getSubTitle(linkedAccounts[0])}
          </StyledSubTitle>
          <Icon style={stylesIcon} icon={'arrowDownOutlined'} color="white" />
        </StyledButton>
      </Dropdown>
    </StyledContainer>
  );
};

export default AccountLinker;

const itemInlay: CSSProperties = {
  padding: 0,
  borderBottomLeftRadius: 6,
  borderBottomRightRadius: 6,
};

const stylesOverlay: CSSProperties = {
  flex: 1,
  maxWidth: 190,
  overflow: 'hidden',
  borderBottomLeftRadius: 6,
  borderBottomRightRadius: 6,
};

const stylesMenu: CSSProperties = {
  maxHeight: '50dvh',
  overflowY: 'auto',
  padding: 0,
};

const stylesIcon: CSSProperties = {
  margin: 0,
  marginLeft: 'auto',
};

const StyledButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: 44px;
  background: ${theme.getColor('primary')} !important;
  flex: 1;
  max-width: 200px;
  min-width: 0;
`;

const StyledAvatarContainer = styled.div`
  min-width: 32px;
  border: 0.5px solid ${theme.gray5};
  border-radius: 50px;
`;

const StyledItem = styled.div`
  display: flex;
  padding: 6px 0;
  gap: 8px;
  justify-content: space-between;
  max-width: 200px;
`;

const StyledDivider = styled.div`
  width: 1.5px;
  background-color: #fff;
  height: 20px;
`;

const StyledSpin = styled(Spin)`
  padding-top: 6px;
  padding-left: 10px;
`;

const StyledLink = styled(Text)`
  font-size: 14px;
  line-height: 1.1;
`;

const StyledButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 5px;
  justify-content: center;
  width: 100%;
  background-color: #f5f5f5;
  border-bottom-left-radius: 6px;
  border-bottom-right-radius: 6px;
  padding: 16px 10px;
`;

const StyledFlex = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  max-width: 115px;
`;

const StyledTitle = styled(Text)<{ $dark?: boolean; $inMenu?: boolean }>`
  font-size: 14px;
  line-height: 1.1;
  text-align: left;
  font-weight: bold;
  color: ${({ $dark }) => ($dark ? '#000' : '#fff')};
  flex: ${({ $inMenu }) => ($inMenu ? undefined : '0 0 auto')};
  max-width: ${({ $inMenu }) => ($inMenu ? undefined : '45% !important')};
`;

const StyledSubTitle = styled(Text)<{ $dark?: boolean }>`
  font-size: 10px;
  line-height: 1.1;
  width: fit-content;
  color: ${({ $dark }) => ($dark ? theme.gray7 : theme.gray3)};
`;

const StyledMenuContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  flex: 1;
  width: 100%;
`;

const StyledContainer = styled.div`
  width: 200px;
  max-width: 200px;
  display: flex;
  max-height: 50px;
`;
